import { useState, useMemo, useRef } from "react";
import { HelpTip } from "@/components/help-tip";
import { TutorialTip } from "@/components/TutorialTip";
import { usePageTour } from "@/components/ProductTour";
import { useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { trackBuilderEvent } from "@/lib/analytics";
import { useAuth } from "@/hooks/use-auth";
import { isTrialBlocked, isMonthlyLimitReached, getQuotaInfo } from "@/lib/quota-banners";
import { getMarketCurrency, formatMoney } from "@/lib/utils";
import { usePageTracking } from "@/hooks/use-track";
import { useQuoteHeadingPreferences } from "@/hooks/use-quote-heading-preferences";
import { quoteHeadingLabel } from "@workspace/api-zod/quote-headings";
import { PercentInput } from "@/components/percent-input";
import { getCostCategories, getProjectTypes, getTaxRate, getTaxLabel, getUnits, getCostTrades, type Client } from "@shared/schema";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { CategorySidebar, useOrderedSections } from "@/components/category-sidebar";
import { type CostNavItem, type CostNavSection } from "@/lib/cost-library-nav";
import {
  ArrowLeft,
  Search,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Save,
  ListPlus,
  AlertCircle,
  Sparkles,
  Loader2,
  SlidersHorizontal,
  HardHat,
  BrickWall,
  Forklift,
  Package,
  Hammer,
  Layers,
  Building2,
  Home,
  TreePine,
  Paintbrush,
  Zap,
  Wrench,
  X,
  BookmarkPlus,
} from "lucide-react";
import { rescaleBreakdown, subtotalFromBreakdown } from "@/lib/cost-breakdown";
import { getCategoryIcon } from "@/lib/category-icons";
import { getItemStage } from "@/lib/section-placement";

// ── Types ─────────────────────────────────────────────────────────────────────

interface LibraryItem {
  id: string;
  category: string;
  trade?: string;
  name: string;
  description?: string;
  unit: string;
  rateLow: string;
  rateTypical: string;
  rateHigh?: string;
  keywords?: string;
  userRate?: string;
  isCustom?: boolean;
  isHidden?: boolean;
}

interface QuoteLineItem {
  id: string;
  libraryId?: string;
  name: string;
  category: string;
  trade?: string;
  description: string;
  unit: string;
  quantity: number;
  unitCost: number;
  labourCost: number;
  materialCost: number;
  plantCost: number;
}

const formSchema = z.object({
  clientId: z.string().optional(),
  projectAddress: z.string().optional(),
  projectType: z.string().optional(),
  description: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

// ── Helpers ───────────────────────────────────────────────────────────────────

const STAGE_ORDER = [
  "Preparation",
  "Demolition",
  "Substructure",
  "Structure",
  "Roofing",
  "External Works",
  "Finishes",
  "Services",
  "Other",
] as const;

const STAGE_ICONS = {
  "Preparation":    HardHat,
  "Demolition":     Hammer,
  "Substructure":   Layers,
  "Structure":      Building2,
  "Roofing":        Home,
  "External Works": TreePine,
  "Finishes":       Paintbrush,
  "Services":       Zap,
  "Other":          Wrench,
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function QuoteManualPage() {
  const { names: headingNames } = useQuoteHeadingPreferences();
  usePageTour("manual-quote");
  usePageTracking("Manual Quote");
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const GBP = { format: (v: number) => formatMoney(v, user?.market) };
  const trialBlocked = isTrialBlocked(user);
  const monthlyLimitInfo = isMonthlyLimitReached(user);
  const quotaInfo = getQuotaInfo(user as any);

  const [taskSearch, setTaskSearch] = useState("");

  // ── Category sidebar state (mirrors Cost Library) ─────────────────────────
  const { sections: orderedSections, reorder: reorderSections } = useOrderedSections();
  const [sidebarSearch, setSidebarSearch] = useState("");
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [selectedSectionLabel, setSelectedSectionLabel] = useState<string>("All Categories");
  const [selectedItemLabel, setSelectedItemLabel] = useState<string>("");
  const [navCategory, setNavCategory] = useState<string>("all");
  const [navTrade, setNavTrade] = useState<string>("all");
  const [navKeyword, setNavKeyword] = useState<string>("");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [expandedBreakdowns, setExpandedBreakdowns] = useState<Set<string>>(new Set());
  const [collapsedStages, setCollapsedStages] = useState<Set<string>>(() => new Set(STAGE_ORDER));
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const COST_CATEGORIES = getCostCategories(user?.market);
  const PROJECT_TYPES = getProjectTypes(user?.market);
  const getCategoryLabel = (value: string): string =>
    COST_CATEGORIES.find((c) => c.value === value)?.label ?? value;

  // Quote state
  const [items, setItems] = useState<QuoteLineItem[]>([]);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Pricing state (toggle-based)
  const [contingencyEnabled, setContingencyEnabled] = useState(true);
  const [contingencyPct, setContingencyPct] = useState("5");
  const [overheadEnabled, setOverheadEnabled] = useState(true);
  const [overheadPct, setOverheadPct] = useState("10");
  const [profitEnabled, setProfitEnabled] = useState(true);
  const [profitPct, setProfitPct] = useState("15");
  const [includeVat, setIncludeVat] = useState(true);
  const [assumptions, setAssumptions] = useState<string[]>([
    "All works to be carried out during normal working hours.",
    "Access to be provided by client.",
  ]);
  const [exclusions, setExclusions] = useState<string[]>([
    "Architect and structural engineer fees unless listed above.",
    "Building control fees.",
    "Any unforeseen works uncovered during construction.",
  ]);
  const [assumptionsOpen, setAssumptionsOpen] = useState(false);
  const [exclusionsOpen, setExclusionsOpen] = useState(false);

  // Adjust Pricing slider state
  const baseItemsRef = useRef<{ unitCost: number }[]>([]);
  const [sliderVal, setSliderVal] = useState<number | null>(null);

  // "Something missing?" state
  const [addDetailText, setAddDetailText] = useState("");

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { clientId: "", projectAddress: "", projectType: "", description: "" },
  });

  const { data: libraryItems = [], isLoading: libraryLoading } = useQuery<LibraryItem[]>({
    queryKey: ["/api/cost-library"],
  });
  const { data: clients = [] } = useQuery<Client[]>({
    queryKey: ["/api/clients"],
  });

  // ── Sidebar selection handlers (mirrors Cost Library) ─────────────────────

  function toggleSection(sectionId: string) {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }

  function handleSelectItem(item: CostNavItem, sectionId: string) {
    setSelectedItemId(item.id);
    setNavCategory(item.categoryFilter || "all");
    setNavTrade(item.tradeFilter || "all");
    setNavKeyword(item.keywordFilter || "");
    const section = orderedSections.find((s) => s.id === sectionId);
    setSelectedSectionLabel(section?.label || "");
    setSelectedItemLabel(item.label);
    setMobileSidebarOpen(false);
  }

  function handleSelectSection(section: CostNavSection) {
    setSelectedItemId(null);
    setSelectedItemLabel("");
    setSelectedSectionLabel(section.label);
    setNavTrade("all");
    const catSet = new Set<string>();
    section.items.forEach((i) => { if (i.categoryFilter) catSet.add(i.categoryFilter); });
    const cats = Array.from(catSet);
    if (cats.length === 1) {
      setNavCategory(cats[0]!);
      setNavKeyword("");
    } else {
      setNavCategory("all");
      const keywords = section.items.map((i) => i.keywordFilter).filter(Boolean).join(" ");
      setNavKeyword(keywords || "");
    }
  }

  function handleShowAll() {
    setSelectedItemId(null);
    setSelectedItemLabel("");
    setSelectedSectionLabel("All Categories");
    setNavCategory("all");
    setNavTrade("all");
    setNavKeyword("");
  }

  // ── Item browser filtering ─────────────────────────────────────────────────

  const visibleItems = useMemo(() => {
    const q = taskSearch.trim().toLowerCase();
    return libraryItems.filter((lib) => {
      if (lib.isHidden) return false;
      if (navCategory !== "all" && lib.category !== navCategory) return false;
      if (navTrade !== "all" && lib.trade !== navTrade) return false;
      if (navKeyword) {
        const keywords = navKeyword.toLowerCase().split(" ").filter(Boolean);
        const text = `${lib.name} ${lib.description ?? ""} ${lib.keywords ?? ""}`.toLowerCase();
        if (!keywords.some((kw) => text.includes(kw))) return false;
      }
      if (q) {
        const text = `${lib.name} ${lib.description ?? ""} ${lib.keywords ?? ""}`.toLowerCase();
        if (!text.includes(q)) return false;
      }
      return true;
    });
  }, [libraryItems, taskSearch, navCategory, navTrade, navKeyword]);

  // ── Pricing calculations ──────────────────────────────────────────────────

  const { subtotal, contingencyAmt, overheadAmt, profitAmt, preTax, vatAmt, grandTotal } = useMemo(() => {
    const subtotal = items.reduce((s, i) => s + i.quantity * i.unitCost, 0);
    const contingencyAmt = contingencyEnabled ? subtotal * (Number(contingencyPct) / 100) : 0;
    const overheadAmt = overheadEnabled ? subtotal * (Number(overheadPct) / 100) : 0;
    const profitAmt = profitEnabled ? subtotal * (Number(profitPct) / 100) : 0;
    const preTax = subtotal + contingencyAmt + overheadAmt + profitAmt;
    const vatAmt = includeVat ? preTax * (getTaxRate(user?.market) / 100) : 0;
    return { subtotal, contingencyAmt, overheadAmt, profitAmt, preTax, vatAmt, grandTotal: preTax + vatAmt };
  }, [items, contingencyEnabled, contingencyPct, overheadEnabled, overheadPct, profitEnabled, profitPct, includeVat]);

  function applyScale(newSubtotal: number, currentItems: QuoteLineItem[]) {
    if (!baseItemsRef.current.length) return;
    const baseTotal = baseItemsRef.current.reduce((s, b, idx) => s + (currentItems[idx]?.quantity ?? 1) * b.unitCost, 0);
    if (baseTotal <= 0) return;
    const factor = newSubtotal / baseTotal;
    setItems(prev => prev.map((item, idx) => {
      const newUnitCost = Math.round((baseItemsRef.current[idx]?.unitCost ?? item.unitCost) * factor);
      const r = rescaleBreakdown(
        { labour: item.labourCost, material: item.materialCost, plant: item.plantCost },
        item.quantity * newUnitCost,
        item.category,
      );
      return { ...item, unitCost: newUnitCost, labourCost: r.labour, materialCost: r.material, plantCost: r.plant };
    }));
  }

  // ── Mutations ─────────────────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const lineItems = items.map((item, index) => ({
        name: item.name, category: item.category, description: item.description,
        unit: item.unit, quantity: String(item.quantity), unitCost: String(item.unitCost),
        labourCost: String(item.labourCost ?? 0),
        materialCost: String(item.materialCost ?? 0),
        plantCost: String(item.plantCost ?? 0),
        labourHours: "0",
        sortOrder: index, section: "GENERAL", inclusions: [], exclusions: [],
      }));
      const payload = {
        clientId: data.clientId || undefined,
        projectAddress: data.projectAddress || "",
        projectType: data.projectType,
        description: data.description || "",
        includeVat,
        contingencyPercent: contingencyEnabled ? contingencyPct : "0",
        overheadPercent: overheadEnabled ? overheadPct : "0",
        profitPercent: profitEnabled ? profitPct : "0",
        lineItems,
        assumptions: assumptions.join("\n") || "",
        exclusions: exclusions.join("\n") || "",
        analysisNotes: "Manual quote — items selected from cost library by user.",
        source: "manual",
        quoteType: "manual",
      };
      const res = await apiRequest("POST", "/api/quotes", payload);
      return res.json();
    },
    onSuccess: (quote: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/quotes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/quotes/recent"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats"] });
      trackBuilderEvent("quote_created", { product: "builder", mode: "manual" });
      toast({ title: "Quote saved" });
      navigate(`/quotes/${quote.id}`);
    },
    onError: () => {
      toast({ title: "Failed to save quote", variant: "destructive" });
    },
  });

  const saveToLibraryMutation = useMutation({
    mutationFn: (item: QuoteLineItem) => apiRequest("POST", "/api/cost-library/add-from-quote", {
      name: item.name,
      category: item.category,
      trade: item.trade || getCostTrades(user?.market)[item.category]?.trades[0]?.value || "general",
      unit: item.unit,
      rateTypical: String(item.unitCost),
      description: item.description || undefined,
    }),
    onSuccess: (_data, item) => {
      queryClient.invalidateQueries({ queryKey: ["/api/cost-library"] });
      toast({ title: "Saved to cost library", description: `"${item.name}" is now in your Cost Library for future quotes.` });
    },
    onError: (err: any) => {
      if (String(err?.message || "").startsWith("409")) {
        toast({ title: "Already in your Cost Library", description: "This item is already saved, so no duplicate was created." });
        return;
      }
      toast({ title: "Failed to save to library", variant: "destructive" });
    },
  });

  const generateWorkItemMutation = useMutation({
    mutationFn: async (description: string) => {
      const response = await apiRequest("POST", "/api/generate-work-item", {
        description,
        projectContext: form.getValues("description") || "",
        useCostLibraryRates: true,
      });
      return response.json();
    },
    onSuccess: (item: any) => {
      if (!item?.name || !item?.unit || item?.unitCost <= 0) {
        toast({ title: "Could not price item", description: "Please try again with more detail", variant: "destructive" });
        return;
      }
      setItems((prev) => {
        const qty = item.quantity || 1;
        const subtotal = qty * item.unitCost;
        const lab = Number(item.labourCost ?? 0);
        const mat = Number(item.materialCost ?? 0);
        const plt = Number(item.plantCost ?? 0);
        // Use server-provided split when present; otherwise derive from the
        // category default so the breakdown is editable from the start.
        const init = lab + mat + plt > 0
          ? { labour: lab, material: mat, plant: plt }
          : rescaleBreakdown({ labour: 0, material: 0, plant: 0 }, subtotal, item.category || "other");
        return [
          ...prev,
          {
            id: crypto.randomUUID(),
            name: item.name,
            category: item.category || "other",
            trade: item.trade,
            description: item.description || "",
            unit: item.unit,
            quantity: qty,
            unitCost: item.unitCost,
            labourCost: init.labour,
            materialCost: init.material,
            plantCost: init.plant,
          },
        ];
      });
      setAddDetailText("");
      toast({ title: "Item added", description: `${item.name} added to your quote` });
    },
    onError: () => toast({ title: "Failed to add item", description: "Please try again", variant: "destructive" }),
  });

  // ── Item operations ───────────────────────────────────────────────────────

  const addItem = (lib: LibraryItem) => {
    setCollapsedStages(prev => {
      const next = new Set(prev);
      next.delete(getItemStage(lib.category, lib.trade, lib.name));
      return next;
    });
    const effectiveRate = lib.userRate ? Number(lib.userRate) : Number(lib.rateTypical);
    const existing = items.find((i) => i.libraryId === lib.id);
    if (existing) {
      // Bumping qty: rescale L/M/P proportionally so the breakdown stays in
      // sync with the new total.
      setItems((prev) => prev.map((i) => {
        if (i.libraryId !== lib.id) return i;
        const newQty = i.quantity + 1;
        const r = rescaleBreakdown(
          { labour: i.labourCost, material: i.materialCost, plant: i.plantCost },
          newQty * i.unitCost,
          i.category,
        );
        return { ...i, quantity: newQty, labourCost: r.labour, materialCost: r.material, plantCost: r.plant };
      }));
      return;
    }
    const init = rescaleBreakdown({ labour: 0, material: 0, plant: 0 }, effectiveRate, lib.category);
    setItems((prev) => [...prev, {
      id: crypto.randomUUID(), libraryId: lib.id, name: lib.name,
      category: lib.category, trade: lib.trade, description: lib.description || "",
      unit: lib.unit, quantity: 1, unitCost: effectiveRate,
      labourCost: init.labour, materialCost: init.material, plantCost: init.plant,
    }]);
  };

  const updateItem = (id: string, field: keyof QuoteLineItem, value: string | number) => {
    setItems((prev) => prev.map((i) => {
      if (i.id !== id) return i;
      const next = { ...i, [field]: value } as QuoteLineItem;
      // Two-way linking: qty/unitCost edit -> rescale L/M/P proportionally.
      if (field === "quantity" || field === "unitCost") {
        const qty = Number(field === "quantity" ? value : next.quantity) || 0;
        const uc = Number(field === "unitCost" ? value : next.unitCost) || 0;
        const r = rescaleBreakdown(
          { labour: i.labourCost, material: i.materialCost, plant: i.plantCost },
          qty * uc,
          i.category,
        );
        next.labourCost = r.labour;
        next.materialCost = r.material;
        next.plantCost = r.plant;
      }
      return next;
    }));
  };

  const updateBreakdownField = (
    id: string,
    field: "labourCost" | "materialCost" | "plantCost",
    value: number,
  ) => {
    setItems((prev) => prev.map((i) => {
      if (i.id !== id) return i;
      const v = Math.max(0, Number(value) || 0);
      const next = { ...i, [field]: v } as QuoteLineItem;
      const newSubtotal = subtotalFromBreakdown(next.labourCost, next.materialCost, next.plantCost);
      const qty = Number(i.quantity) || 0;
      next.unitCost = qty > 0 ? newSubtotal / qty : Number(i.unitCost) || 0;
      return next;
    }));
  };

  const removeItem = (id: string) => setItems((prev) => prev.filter((i) => i.id !== id));

  const toggleStage = (stage: string) => setCollapsedStages(prev => {
    const n = new Set(prev);
    n.has(stage) ? n.delete(stage) : n.add(stage);
    return n;
  });

  const toggleItem = (id: string) => setExpandedItems(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const moveItem = (id: string, dir: "up" | "down") => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.id === id);
      if (idx < 0) return prev;
      const next = [...prev];
      const target = dir === "up" ? idx - 1 : idx + 1;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const onSubmit = (data: FormData) => {
    if (items.length === 0) {
      toast({ title: "Add at least one item before saving", variant: "destructive" });
      return;
    }
    saveMutation.mutate(data);
  };


  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full">

      {/* Header */}
      <div className="flex items-center gap-3 px-5 py-3 border-b bg-background shrink-0">
        <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="button-back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-2 min-w-0">
          <ListPlus className="h-5 w-5 text-primary shrink-0" />
          <div className="min-w-0">
            <h1 className="text-lg font-bold leading-tight whitespace-nowrap flex items-center gap-2" data-testid="text-manual-quote-title">Manual Quote
              <HelpTip testId="button-help-manual-quote" content="Search or scroll through the cost library and click to add items directly to your quote. Manual Quote gives you full control over every line item, ideal for bespoke or unusual projects where a template or Auto Quote may not be the best fit." />
            </h1>
            <p className="text-xs text-muted-foreground whitespace-nowrap">Pick items from the cost library</p>
          </div>
        </div>
      </div>

      <div className="px-5 pt-3 shrink-0">
        <TutorialTip id="manual-quote-intro" title="Using Manual Quotes">
          Search or scroll through the cost library and click the plus button to add items to your quote. Use this method for bespoke or unusual jobs where a template does not quite fit. You have full control over every line item, quantity, and price before saving.
        </TutorialTip>
      </div>

      {/* Body */}
      <div className="flex flex-1 min-h-0">

        {/* Desktop category sidebar — hidden for trial-blocked users */}
        {!trialBlocked && <div className="w-72 border-r bg-card/50 flex-shrink-0 hidden md:flex flex-col">
          <CategorySidebar
            sections={orderedSections}
            sidebarSearch={sidebarSearch}
            setSidebarSearch={setSidebarSearch}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
            selectedItemId={selectedItemId}
            onSelectItem={handleSelectItem}
            onSelectSection={handleSelectSection}
            onDragEnd={reorderSections}
          />
        </div>}

        {/* Workspace */}
        <div className="flex-1 overflow-y-auto min-w-0">
          <div className="px-4 sm:px-6 py-4 sm:py-6 space-y-4">

            {trialBlocked ? (
              <Card className="border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20" data-testid="banner-trial-quota-exceeded">
                <CardContent className="pt-5 pb-4">
                  <div className="flex gap-3 items-start">
                    <div className="flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                    </div>
                    <div className="flex-1 space-y-2">
                      <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Free trial quote limit reached</p>
                      <p className="text-sm text-amber-700 dark:text-amber-400">You've reached the shared allowance of 5 total quote creations across Auto, Template, and Manual quotes. Subscribe to a plan to keep creating quotes.</p>
                      <a href="/settings" className="inline-block mt-1 text-sm font-medium underline text-amber-800 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-200" data-testid="link-upgrade-from-quota-manual">
                        Upgrade your plan →
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {monthlyLimitInfo && (
                  <Card className="border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20" data-testid="card-monthly-limit-info-manual">
                    <CardContent className="pt-5 pb-4">
                      <div className="flex gap-3 items-start">
                        <div className="flex-shrink-0 mt-0.5 text-amber-600 dark:text-amber-400">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                        </div>
                        <div className="flex-1 space-y-1">
                          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Monthly AI quote limit reached</p>
                          <p className="text-sm text-amber-700 dark:text-amber-400">You've reached your monthly AI quote limit. Manual and Template quotes are unaffected — your AI allowance resets on the 1st of next month. To get more AI quotes now, upgrade your plan.</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {!monthlyLimitInfo && quotaInfo && (
                  <p className="text-xs text-muted-foreground" data-testid="text-quota-pill">
                    {quotaInfo.quotesLeft} of {quotaInfo.quotesTotal} quote{quotaInfo.quotesTotal !== 1 ? "s" : ""} remaining
                    {quotaInfo.period === "trial" ? " in your free trial" : " this month"}
                  </p>
                )}

            {/* Project details */}
            <Card>
              <button
                type="button"
                onClick={() => setDetailsOpen((o) => !o)}
                className="w-full flex items-center justify-between px-6 py-4 hover:bg-muted/30 transition-colors rounded-xl"
                data-testid="button-toggle-details"
              >
                <span className="text-base font-semibold leading-none tracking-tight">Project Details</span>
                {detailsOpen ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
              </button>

              {detailsOpen && (
                <CardContent className="pt-0 border-t">
                  <Form {...form}>
                    <div className="pt-4 grid grid-cols-2 gap-3">
                      <FormField
                        control={form.control}
                        name="clientId"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs">Client</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger className="h-8 text-sm" data-testid="select-client">
                                  <SelectValue placeholder="Select client" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {clients.map((c) => (
                                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="projectType"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs">Project Type</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger className="h-8 text-sm" data-testid="select-project-type">
                                  <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {PROJECT_TYPES.map((t) => (
                                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="projectAddress"
                        render={({ field }) => (
                          <FormItem className="col-span-2">
                            <FormLabel className="text-xs">Project Address</FormLabel>
                            <FormControl>
                              <Input {...field} className="h-8 text-sm" placeholder="Site address" data-testid="input-project-address" />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>
                  </Form>
                </CardContent>
              )}
            </Card>

            {/* Mobile categories sheet */}
            <div className="md:hidden">
              <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
                <SheetTrigger asChild>
                  <Button size="sm" className="w-full gap-2 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="button-mobile-categories">
                    <SlidersHorizontal className="h-4 w-4" />
                    {selectedSectionLabel && selectedSectionLabel !== "All Categories" ? selectedSectionLabel : "Browse Categories"}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="p-0 w-80">
                  <CategorySidebar
                    sections={orderedSections}
                    sidebarSearch={sidebarSearch}
                    setSidebarSearch={setSidebarSearch}
                    expandedSections={expandedSections}
                    toggleSection={toggleSection}
                    selectedItemId={selectedItemId}
                    onSelectItem={handleSelectItem}
                    onSelectSection={handleSelectSection}
                    onDragEnd={reorderSections}
                  />
                </SheetContent>
              </Sheet>
            </div>

            {/* Item browser */}
            <Card>
              {/* Breadcrumb + search header */}
              <div className="px-5 py-3 border-b flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                  <button
                    onClick={handleShowAll}
                    className="font-semibold text-sm text-foreground hover:text-muted-foreground transition-colors"
                    data-testid="button-show-all-tasks"
                  >
                    All Tasks
                  </button>
                  {selectedSectionLabel && selectedSectionLabel !== "All Categories" && (
                    <>
                      <ChevronRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                      <button
                        onClick={() => {
                          const section = orderedSections.find((s) => s.label === selectedSectionLabel);
                          if (section) handleSelectSection(section);
                        }}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                        data-testid="button-breadcrumb-section"
                      >
                        {selectedSectionLabel}
                      </button>
                    </>
                  )}
                  {selectedItemLabel && (
                    <>
                      <ChevronRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm text-foreground" data-testid="text-breadcrumb-item">{selectedItemLabel}</span>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-muted-foreground">{visibleItems.length} item{visibleItems.length !== 1 ? "s" : ""}</span>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search tasks..."
                      value={taskSearch}
                      onChange={(e) => setTaskSearch(e.target.value)}
                      className="pl-8 h-8 text-sm w-52"
                      data-testid="input-task-search"
                    />
                  </div>
                </div>
              </div>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                {/* Column headers */}
                <div className="grid grid-cols-[1fr_56px_100px_44px] gap-2 px-4 sm:px-6 py-2 text-[11px] font-medium text-muted-foreground bg-muted/10 border-b min-w-[320px]">
                  <span>Task</span>
                  <span>Unit</span>
                  <span className="text-right">Typical Rate</span>
                  <span />
                </div>

                {libraryLoading ? (
                  <div className="p-4 space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="h-12 rounded bg-muted animate-pulse" />
                    ))}
                  </div>
                ) : navCategory === "all" && !taskSearch ? (
                  <div className="py-14 text-center space-y-2">
                    <SlidersHorizontal className="h-8 w-8 text-muted-foreground/40 mx-auto" />
                    <p className="text-sm font-medium text-muted-foreground">Select a category to browse tasks</p>
                    <p className="text-xs text-muted-foreground/70">Use the sidebar on the left or the Browse Categories button above</p>
                  </div>
                ) : visibleItems.length === 0 ? (
                  <div className="py-12 text-center text-sm text-muted-foreground">
                    No items found.
                  </div>
                ) : (
                  <div className="divide-y">
                    {visibleItems.map((lib) => {
                      const effectiveRate = lib.userRate ? Number(lib.userRate) : Number(lib.rateTypical);
                      const alreadyAdded = items.some((i) => i.libraryId === lib.id);
                      return (
                        <div
                          key={lib.id}
                          className="grid grid-cols-[1fr_56px_100px_44px] gap-2 items-center px-4 sm:px-6 py-3 hover:bg-muted/30 transition-colors min-w-[320px]"
                          data-testid={`library-item-${lib.id}`}
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-medium leading-tight truncate">{lib.name}</div>
                            {lib.description && (
                              <div className="text-xs text-muted-foreground truncate mt-0.5">{lib.description}</div>
                            )}
                            <div className="text-[10px] text-muted-foreground/70 mt-0.5">{getCategoryLabel(lib.category)}</div>
                          </div>
                          <span className="text-xs text-muted-foreground">{lib.unit}</span>
                          <span className="text-sm font-medium text-right tabular-nums">{GBP.format(effectiveRate)}</span>
                          <div className="flex justify-center">
                            <Button
                              size="icon"
                              variant={alreadyAdded ? "default" : "outline"}
                              className="h-7 w-7"
                              onClick={() => addItem(lib)}
                              title={alreadyAdded ? "Increase quantity" : "Add to quote"}
                              data-testid={`button-add-library-item-${lib.id}`}
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                </div>{/* /overflow-x-auto */}
              </CardContent>
            </Card>

            {/* Quote items */}
            <Card className="bg-background">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">Quote Items</CardTitle>
                  {items.length > 0 && (
                    <Badge variant="secondary" className="h-5 px-1.5 text-xs">{items.length}</Badge>
                  )}
                </div>
                <CardDescription>Click + in the library above to add items here</CardDescription>
              </CardHeader>
              <CardContent>
                {items.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
                    <ListPlus className="h-9 w-9 mb-2.5 opacity-25" />
                    <p className="text-sm">Click the + button next to any item in the library above to add it here.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(() => {
                      const sym = getMarketCurrency(user?.market).symbol;
                      const activeStages = STAGE_ORDER.filter(stage =>
                        items.some(item => getItemStage(item.category, item.trade, item.name) === stage)
                      );
                      return activeStages.map(stage => {
                        const stageItems = items
                          .map((item, idx) => ({ item, idx }))
                          .filter(({ item }) => getItemStage(item.category, item.trade, item.name) === stage);
                        const isCollapsed = collapsedStages.has(stage);
                        const stageSubtotal = stageItems.reduce((s, { item }) => s + item.quantity * item.unitCost, 0);
                        return (
                          <div key={stage} className="border border-primary/30 rounded-lg overflow-hidden">
                            {/* Stage header */}
                            <button
                              type="button"
                              onClick={() => toggleStage(stage)}
                              aria-expanded={!isCollapsed}
                              className="w-full flex items-center gap-3 py-3 px-4 bg-sidebar hover:bg-sidebar-accent transition-colors"
                            >
                              {(() => { const SI = STAGE_ICONS[stage as keyof typeof STAGE_ICONS]; return SI ? <SI className="h-4 w-4 text-primary shrink-0" /> : null; })()}
                              <span className="font-semibold text-sm flex-1 text-left text-primary">{quoteHeadingLabel(headingNames, stage)}</span>
                              <span className="text-xs text-slate-400">{stageItems.length} {stageItems.length === 1 ? "item" : "items"}</span>
                              <span className="text-xs font-semibold text-primary ml-2">{GBP.format(stageSubtotal)}</span>
                              {isCollapsed
                                ? <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
                                : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
                              }
                            </button>
                            {/* Stage items */}
                            {!isCollapsed && (
                              <div className="space-y-2 p-4 border-t border-primary/20">
                                {stageItems.map(({ item, idx }, sortedIndex, sortedArr) => {
                                  const isItemExpanded = expandedItems.has(item.id);
                                  const itemSubtotal = item.quantity * item.unitCost;
                                  const ItemIcon = getCategoryIcon(item.category);
                                  const isBreakdownOpen = expandedBreakdowns.has(item.id);
                                  return (
                                  <div key={item.id} className="rounded-md border border-border overflow-hidden" data-testid={`quote-item-${idx}`}>
                                    {/* Compact summary row */}
                                    <div className="flex items-center gap-2 px-3 py-2 bg-card">
                                      <span className="shrink-0 p-1">
                                        {ItemIcon ? <ItemIcon className="h-4 w-4 text-primary" /> : <Package className="h-4 w-4 text-muted-foreground" />}
                                      </span>
                                      <span
                                        className="flex-1 text-xs sm:text-sm font-medium [overflow-wrap:anywhere] line-clamp-2 leading-tight sm:line-clamp-none sm:truncate sm:leading-normal cursor-pointer select-none min-w-0"
                                        onClick={() => toggleItem(item.id)}
                                      >
                                        {item.name}
                                      </span>
                                      <div className="relative shrink-0 w-16 sm:w-28">
                                        <span className="absolute left-1.5 sm:left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">{sym}</span>
                                        <Input
                                          type="number"
                                          value={itemSubtotal > 0 ? Math.round(itemSubtotal) : ""}
                                          onChange={e => {
                                            const newSub = parseFloat(e.target.value) || 0;
                                            if (item.quantity > 0) updateItem(item.id, "unitCost", newSub / item.quantity);
                                          }}
                                          placeholder="0"
                                          className="h-8 pl-4 sm:pl-6 pr-0.5 sm:pr-2 text-xs sm:text-sm text-right"
                                          data-testid={`input-subtotal-${idx}`}
                                        />
                                      </div>
                                      <button
                                        type="button"
                                        title="Save to cost library"
                                        aria-label="Save to cost library"
                                        disabled={saveToLibraryMutation.isPending}
                                        onClick={() => saveToLibraryMutation.mutate(item)}
                                        className="shrink-0 p-1 rounded text-muted-foreground hover:text-primary hover:bg-muted/50 transition-colors disabled:opacity-40"
                                        data-testid={`button-save-library-row-${idx}`}
                                      >
                                        <BookmarkPlus className="h-4 w-4" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => toggleItem(item.id)}
                                        className="shrink-0 p-1 rounded hover:bg-muted/50 transition-colors"
                                      >
                                        {isItemExpanded
                                          ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                          : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                        }
                                      </button>
                                    </div>
                                    {/* Expanded detail panel */}
                                    {isItemExpanded && (
                                      <div className="border-t border-border px-3 pt-3 pb-3 space-y-3 bg-muted/20">
                                        <Input
                                          value={item.name}
                                          onChange={e => updateItem(item.id, "name", e.target.value)}
                                          className="h-8 text-sm font-medium w-full"
                                          data-testid={`input-item-name-${idx}`}
                                        />
                                        <Input
                                          value={item.description}
                                          onChange={e => updateItem(item.id, "description", e.target.value)}
                                          placeholder="Description"
                                          className="h-8 text-xs text-muted-foreground w-full"
                                          data-testid={`input-item-desc-${idx}`}
                                        />
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                          <div>
                                            <label className="text-xs text-muted-foreground">Quantity</label>
                                            <Input
                                              type="number" min="0" step="0.5"
                                              value={item.quantity || ""}
                                              onChange={e => updateItem(item.id, "quantity", parseFloat(e.target.value) || 0)}
                                              className="h-8 text-sm"
                                              data-testid={`input-qty-${idx}`}
                                            />
                                          </div>
                                          <div>
                                            <label className="text-xs text-muted-foreground">Unit</label>
                                            <Select value={item.unit} onValueChange={v => updateItem(item.id, "unit", v)}>
                                              <SelectTrigger className="h-8 text-sm" data-testid={`select-unit-${idx}`}>
                                                <SelectValue />
                                              </SelectTrigger>
                                              <SelectContent>
                                                {Object.entries(getUnits(user?.market)).map(([key, label]) => (
                                                  <SelectItem key={key} value={key}>{label}</SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                          <div>
                                            <label className="text-xs text-muted-foreground">Unit Cost ({sym})</label>
                                            <Input
                                              type="number" min="0" step="0.01"
                                              value={item.unitCost || ""}
                                              onChange={e => updateItem(item.id, "unitCost", parseFloat(e.target.value) || 0)}
                                              className="h-8 text-sm"
                                              data-testid={`input-unit-cost-${idx}`}
                                            />
                                          </div>
                                          <div>
                                            <label className="text-xs text-muted-foreground">Subtotal</label>
                                            <Input
                                              type="number"
                                              value={item.quantity > 0 ? Math.round(item.quantity * item.unitCost) : ""}
                                              onChange={e => {
                                                const newSub = parseFloat(e.target.value) || 0;
                                                if (item.quantity > 0) updateItem(item.id, "unitCost", newSub / item.quantity);
                                              }}
                                              className="h-8 text-sm"
                                              data-testid={`input-subtotal-expanded-${idx}`}
                                            />
                                          </div>
                                        </div>
                                        <div data-testid={`text-breakdown-${idx}`}>
                                          <div className="flex items-center justify-between pt-2 border-t">
                                            <button
                                              type="button"
                                              onClick={() => setExpandedBreakdowns(prev => {
                                                const next = new Set(prev);
                                                next.has(item.id) ? next.delete(item.id) : next.add(item.id);
                                                return next;
                                              })}
                                              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                                            >
                                              {isBreakdownOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                              Cost breakdown
                                            </button>
                                            <div className="flex items-center gap-0.5">
                                          <button type="button"
                                            className="p-0.5 rounded text-muted-foreground disabled:opacity-30 disabled:pointer-events-none hover:text-foreground"
                                            disabled={sortedIndex === 0}
                                            onClick={() => {
                                              const prevId = sortedArr[sortedIndex - 1]?.item.id;
                                              if (!prevId) return;
                                              setItems(prev => {
                                                const arr = [...prev];
                                                const a = arr.findIndex(i => i.id === item.id);
                                                const b = arr.findIndex(i => i.id === prevId);
                                                if (a >= 0 && b >= 0) [arr[a], arr[b]] = [arr[b], arr[a]];
                                                return arr;
                                              });
                                            }}
                                            data-testid={`button-move-up-${idx}`}>
                                            <ChevronUp className="h-4 w-4" />
                                          </button>
                                          <button type="button"
                                            className="p-0.5 rounded text-muted-foreground disabled:opacity-30 disabled:pointer-events-none hover:text-foreground"
                                            disabled={sortedIndex === sortedArr.length - 1}
                                            onClick={() => {
                                              const nextId = sortedArr[sortedIndex + 1]?.item.id;
                                              if (!nextId) return;
                                              setItems(prev => {
                                                const arr = [...prev];
                                                const a = arr.findIndex(i => i.id === item.id);
                                                const b = arr.findIndex(i => i.id === nextId);
                                                if (a >= 0 && b >= 0) [arr[a], arr[b]] = [arr[b], arr[a]];
                                                return arr;
                                              });
                                            }}
                                            data-testid={`button-move-down-${idx}`}>
                                            <ChevronDown className="h-4 w-4" />
                                          </button>
                                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary"
                                            title="Save to cost library"
                                            disabled={saveToLibraryMutation.isPending}
                                            onClick={() => saveToLibraryMutation.mutate(item)}
                                            data-testid={`button-save-library-${idx}`}>
                                            <BookmarkPlus className="h-4 w-4" />
                                          </Button>
                                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7"
                                            onClick={() => removeItem(item.id)}
                                            data-testid={`button-remove-item-${idx}`}>
                                            <Trash2 className="h-4 w-4 text-destructive" />
                                          </Button>
                                        </div>
                                      </div>
                                      {expandedBreakdowns.has(item.id) && (
                                        <div className="mt-2 flex flex-nowrap gap-x-2 overflow-x-auto pb-0.5">
                                          <div className="inline-flex items-center gap-1 min-w-0">
                                            <span title="Labour" className="shrink-0"><HardHat className="h-3.5 w-3.5 text-primary" /></span>
                                            <span className="hidden sm:inline text-xs text-muted-foreground shrink-0">Labour</span>
                                            <div className="relative">
                                              <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">{sym}</span>
                                              <Input type="number" min="0" step="1" value={item.labourCost}
                                                onChange={e => updateBreakdownField(item.id, "labourCost", parseFloat(e.target.value) || 0)}
                                                className={`h-7 w-16 sm:w-20 ${sym.length >= 2 ? "pl-6" : "pl-4"} pr-1 text-xs`}
                                                data-testid={`input-labour-cost-${idx}`} />
                                            </div>
                                          </div>
                                          <div className="inline-flex items-center gap-1 min-w-0">
                                            <span title="Materials" className="shrink-0"><BrickWall className="h-3.5 w-3.5 text-primary" /></span>
                                            <span className="hidden sm:inline text-xs text-muted-foreground shrink-0">Materials</span>
                                            <div className="relative">
                                              <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">{sym}</span>
                                              <Input type="number" min="0" step="1" value={item.materialCost}
                                                onChange={e => updateBreakdownField(item.id, "materialCost", parseFloat(e.target.value) || 0)}
                                                className={`h-7 w-16 sm:w-20 ${sym.length >= 2 ? "pl-6" : "pl-4"} pr-1 text-xs`}
                                                data-testid={`input-material-cost-${idx}`} />
                                            </div>
                                          </div>
                                          <div className="inline-flex items-center gap-1 min-w-0">
                                            <span title="Plant" className="shrink-0"><Forklift className="h-3.5 w-3.5 text-primary" /></span>
                                            <span className="hidden sm:inline text-xs text-muted-foreground shrink-0">Plant</span>
                                            <div className="relative">
                                              <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">{sym}</span>
                                              <Input type="number" min="0" step="1" value={item.plantCost}
                                                onChange={e => updateBreakdownField(item.id, "plantCost", parseFloat(e.target.value) || 0)}
                                                className={`h-7 w-16 sm:w-20 ${sym.length >= 2 ? "pl-6" : "pl-4"} pr-1 text-xs`}
                                                data-testid={`input-plant-cost-${idx}`} />
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                    )}
                                  </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Something missing? */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Something Missing?</CardTitle>
                <CardDescription>
                  Describe a work item you'd like to add and the system will price it and include it in your quote.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-2">
                  <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-3 py-2">
                    <AlertCircle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800 dark:text-amber-300">
                      Only one work item can be added per request. Please describe each item separately for accurate pricing.
                    </p>
                  </div>
                  <Textarea
                    value={addDetailText}
                    onChange={(e) => setAddDetailText(e.target.value)}
                    placeholder="e.g. Install new radiator in the kitchen"
                    className="resize-none w-full"
                    rows={2}
                    data-testid="input-add-detail"
                  />
                  <Button
                    type="button"
                    onClick={() => {
                      if (addDetailText.trim().length >= 3) {
                        generateWorkItemMutation.mutate(addDetailText.trim());
                      }
                    }}
                    disabled={generateWorkItemMutation.isPending || addDetailText.trim().length < 3}
                    className="w-full"
                    data-testid="button-add-detail"
                  >
                    {generateWorkItemMutation.isPending ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Adding...</>
                    ) : (
                      <><Sparkles className="h-4 w-4 mr-2" />Add</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Adjust Pricing */}
            {items.length > 0 && (() => {
              if (!baseItemsRef.current.length) baseItemsRef.current = items.map(i => ({ unitCost: i.unitCost }));
              const baseSubtotal = baseItemsRef.current.reduce((s, b, idx) => s + (items[idx]?.quantity ?? 1) * b.unitCost, 0) || subtotal || 1000;
              const sliderMin = Math.round(baseSubtotal * 0.5);
              const sliderMax = Math.round(baseSubtotal * 1.5);
              const displayVal = sliderVal !== null ? Math.min(Math.max(sliderVal, sliderMin), sliderMax) : Math.min(Math.max(subtotal, sliderMin), sliderMax);
              const pctChange = Math.round(((displayVal - baseSubtotal) / baseSubtotal) * 100);
              return (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Adjust Pricing</CardTitle>
                    <CardDescription>Use the slider to proportionally adjust all work item prices</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium">Subtotal</label>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{GBP.format(subtotal)}</span>
                          {pctChange !== 0 && (
                            <span className={`text-sm font-semibold ${pctChange > 0 ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                              {pctChange > 0 ? `+${pctChange}%` : `${pctChange}%`}
                            </span>
                          )}
                        </div>
                      </div>
                      <Slider
                        value={[displayVal]}
                        min={sliderMin}
                        max={sliderMax}
                        step={50}
                        onValueChange={([val]) => {
                          setSliderVal(val);
                          applyScale(val, items);
                        }}
                        data-testid="slider-subtotal"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>-50% {GBP.format(sliderMin)}</span>
                        <span>+50% {GBP.format(sliderMax)}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Pricing options */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Pricing Options</CardTitle>
                <CardDescription>Enable and set additional charges as needed</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Contingency */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Switch checked={contingencyEnabled} onCheckedChange={setContingencyEnabled} data-testid="switch-contingency" />
                      <Label className="shrink-0">Contingency</Label>
                    </div>
                    {contingencyEnabled && (
                      <div className="flex items-center gap-1 shrink-0">
                        <PercentInput value={contingencyPct} onChange={setContingencyPct} className="w-16 h-8 text-xs" data-testid="input-contingency-percent" />
                        <span className="text-xs text-muted-foreground text-right tabular-nums w-16">{GBP.format(contingencyAmt)}</span>
                      </div>
                    )}
                  </div>

                  {/* Overhead */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Switch checked={overheadEnabled} onCheckedChange={setOverheadEnabled} data-testid="switch-overhead" />
                      <Label className="shrink-0">Overhead</Label>
                    </div>
                    {overheadEnabled && (
                      <div className="flex items-center gap-1 shrink-0">
                        <PercentInput value={overheadPct} onChange={setOverheadPct} className="w-16 h-8 text-xs" data-testid="input-overhead-percent" />
                        <span className="text-xs text-muted-foreground text-right tabular-nums w-16">{GBP.format(overheadAmt)}</span>
                      </div>
                    )}
                  </div>

                  {/* Profit */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Switch checked={profitEnabled} onCheckedChange={setProfitEnabled} data-testid="switch-profit" />
                      <Label className="shrink-0">Profit margin</Label>
                    </div>
                    {profitEnabled && (
                      <div className="flex items-center gap-1 shrink-0">
                        <PercentInput value={profitPct} onChange={setProfitPct} className="w-16 h-8 text-xs" data-testid="input-profit-percent" />
                        <span className="text-xs text-muted-foreground text-right tabular-nums w-16">{GBP.format(profitAmt)}</span>
                      </div>
                    )}
                  </div>

                  {/* VAT */}
                  <div className="flex items-center gap-3 pt-2 border-t">
                    <Switch checked={includeVat} onCheckedChange={setIncludeVat} data-testid="switch-vat" />
                    <Label>Include {getTaxLabel(user?.market)}</Label>
                    {includeVat && <span className="ml-auto text-xs text-muted-foreground tabular-nums">{GBP.format(vatAmt)}</span>}
                  </div>
                </div>

                {/* Totals */}
                <div className="pt-4 mt-4 border-t space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span>{GBP.format(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total (ex VAT)</span>
                    <span>{GBP.format(preTax)}</span>
                  </div>
                  {includeVat && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{getTaxLabel(user?.market)}</span>
                      <span>{GBP.format(vatAmt)}</span>
                    </div>
                  )}
                  <Separator className="my-1" />
                  <div className="flex justify-between text-base font-bold">
                    <span>Grand Total</span>
                    <span className="text-primary" data-testid="text-grand-total">{GBP.format(grandTotal)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Assumptions & Exclusions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Assumptions &amp; Exclusions</CardTitle>
                <CardDescription>Clarify what is and is not included in this quote</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Assumptions accordion */}
                <div className="border rounded-md">
                  <button
                    type="button"
                    onClick={() => setAssumptionsOpen(o => !o)}
                    className="flex items-center justify-between w-full px-3 py-2 text-left hover:bg-muted/30 rounded-md transition-colors"
                  >
                    <p className="text-sm font-medium">Assumptions ({assumptions.length})</p>
                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${assumptionsOpen ? "rotate-180" : ""}`} />
                  </button>
                  {assumptionsOpen && (
                    <div className="px-3 pb-3 space-y-2 max-h-48 overflow-y-auto border-t pt-2">
                      {assumptions.map((a, i) => (
                        <div key={i} className="flex gap-2">
                          <Input
                            value={a}
                            onChange={(e) => {
                              const updated = [...assumptions];
                              updated[i] = e.target.value;
                              setAssumptions(updated);
                            }}
                            className="text-sm h-8"
                            data-testid={`input-assumption-${i}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            onClick={() => setAssumptions(prev => prev.filter((_, idx) => idx !== i))}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-xs h-7"
                        onClick={() => setAssumptions(prev => [...prev, ""])}
                      >
                        + Add Assumption
                      </Button>
                    </div>
                  )}
                </div>

                {/* Exclusions accordion */}
                <div className="border rounded-md">
                  <button
                    type="button"
                    onClick={() => setExclusionsOpen(o => !o)}
                    className="flex items-center justify-between w-full px-3 py-2 text-left hover:bg-muted/30 rounded-md transition-colors"
                  >
                    <p className="text-sm font-medium">Exclusions ({exclusions.length})</p>
                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${exclusionsOpen ? "rotate-180" : ""}`} />
                  </button>
                  {exclusionsOpen && (
                    <div className="px-3 pb-3 space-y-2 max-h-48 overflow-y-auto border-t pt-2">
                      {exclusions.map((ex, i) => (
                        <div key={i} className="flex gap-2">
                          <Input
                            value={ex}
                            onChange={(e) => {
                              const updated = [...exclusions];
                              updated[i] = e.target.value;
                              setExclusions(updated);
                            }}
                            className="text-sm h-8"
                            data-testid={`input-exclusion-${i}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            onClick={() => setExclusions(prev => prev.filter((_, idx) => idx !== i))}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-xs h-7"
                        onClick={() => setExclusions(prev => [...prev, ""])}
                      >
                        + Add Exclusion
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Save */}
            <div className="flex justify-end pb-8">
              <Button
                size="lg"
                onClick={form.handleSubmit(onSubmit)}
                disabled={saveMutation.isPending || items.length === 0}
                data-testid="button-save-quote-bottom"
              >
                <Save className="h-4 w-4 mr-2" />
                {saveMutation.isPending ? "Saving..." : "Save Quote"}
              </Button>
            </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
